import { Box, Card, Checkbox, Chip, Dialog, FormControl, InputLabel, MenuItem, Button as MUIButton, Select, Tooltip, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import dayjs from 'dayjs';
import { ReactElement, useState } from 'react';
import {
  BulkDeleteButton,
  CreateButton,
  Datagrid,
  EditButton,
  Filter,
  FunctionField,
  List,
  TextField,
  TopToolbar,
  useListController,
  useTranslate,
  DeleteButton,
  SelectField,
  TextInput,
  SelectInput,
  Button
} from 'react-admin';
import { CustomBreadcrumbs } from '../../../../../components/custom-breadcrumbs';
import { DashboardContent } from '../../../../../layouts/dashboard';
import { Resources } from '../../resource';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import ErrorPage403 from 'src/pages/error/403';
import { PermissionButton } from 'src/routes/components/permission-button';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddressField from 'src/pages/admin/components/AddressField';
import FilterGroup from 'src/pages/admin/components/FilterGroup';
import XTextInput from 'src/pages/admin/components/XTextInput';
import { chains } from 'src/assets/data';
import CreateAdminConfig from './Create';
import EditAdminConfig from './Edit';
import AuditLogDialog from 'src/pages/admin/components/AuditLog/AuditLogDialog';
import { Iconify } from 'src/components/iconify/iconify';


const ListFilter = (props: any) => {
  const { filterValues } = useListController();

  return (
    <Filter {...(props || {})}>
      <FilterGroup alwaysOn spacing={0} p={0}>
        <FilterGroup>
          <TextInput source="code" label="module.adminConfig.code" />
        </FilterGroup>
      </FilterGroup>
    </Filter>
  );
};

const View = () => {
  const [popup, setPopup] = useState<ReactElement>();
  const translate = useTranslate();
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [auditTitle, setAuditTitle] = useState('Audit Logs');
  const [selectedRecord, setSelectedRecord] = useState<any>(null);

  return (
    <PermissionGuard resource="admin-configs" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('resource.adminConfig')}
          links={[
            { name: translate('resource.configs'), href: '#' },
            { name: translate('resource.adminConfig'), href: `/${Resources.adminConfig}` },
            { name: translate('resource.list') },
          ]}
          action={
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={2}
              mb={1}
            >
              <PermissionGuard resource="admin-configs" action="create" fallback={<></>}>
                <CreateButton
                  variant="contained"
                  size="medium"
                  onClick={(e) => {
                    e.preventDefault();
                    setPopup(<CreateAdminConfig onClose={() => setPopup(undefined)} />);
                  }}
                />
              </PermissionGuard>
              <PermissionGuard resource="audit-logs" action="read" fallback={<></>}>
                <Button
                  variant="contained"
                  size="medium"
                  color="secondary"
                  onClick={() => {
                    setSelectedRecord({});
                    setAuditTitle(`${translate('resource.auditLogs')} - ${translate('resource.adminConfig')}`);
                    setAuditLogOpen(true);
                  }}
                  label={translate('resource.auditLogs')}
                >
                  <Iconify icon="ph:note" />
                </Button>
              </PermissionGuard>
            </Stack>
          }
        />

        <Card sx={{ mt: 3, pb: 2 }}>
          <List
            actions={false}
            empty={false}
            filters={<ListFilter />}
            perPage={10}
            exporter={false}
          >
            <Box sx={{ overflow: 'auto' }}>
              <Datagrid
                rowClick={false}
                bulkActionButtons={
                  <PermissionButton resource="admin-configs" action="delete">
                    <BulkDeleteButton />
                  </PermissionButton>
                }
                sx={{
                  '.RaBulkActionsToolbar-toolbar': {
                    position: 'relative !important',
                    transform: 'none !important',
                  },
                }}
              >
                <TextField source="code" label="module.adminConfig.code" sortable={false} />
                <TextField
                  source="description"
                  label="module.adminConfig.description"
                  sortable={false}
                />
                <FunctionField
                  label="module.adminConfig.enable"
                  render={(record) => (
                    <Chip
                      label={record.enable ? 'True' : 'False'}
                      color={record.enable ? 'success' : 'error'}
                      size="small"
                    />
                  )}
                  sortable={false}
                />
                <TextField source="dataType" label="module.adminConfig.dataType" sortable={false} />

                {/* <TextField source="value" label="module.adminConfig.value" sortable={false}/> */}
                <FunctionField
                  label="module.adminConfig.value"
                  render={(record: any) => {
                    if (!record) return null;
                    if (record.dataType === 'object') {
                      return <span>{JSON.stringify(record.value, null, 2)}</span>;
                    }
                    if (record.dataType === 'number') {
                      return <span>{Number(record.value)}</span>;
                    }
                    return <span>{String(record.value)}</span>;
                  }}
                  sortable={false}
                />
                <FunctionField
                  render={(record: any) => (
                    <Stack direction="row" spacing={2}>
                      <PermissionGuard resource="admin-configs" action="update" fallback={<></>}>
                        <EditButton
                          onClick={(e) => {
                            e.preventDefault();
                            setPopup(
                              <EditAdminConfig id={record.id} onClose={() => setPopup(undefined)} />
                            );
                          }}
                        />
                      </PermissionGuard>
                      {/* <PermissionGuard resource="admin-configs" action="delete" fallback={<></>}>
                        <DeleteButton
                          mutationMode="pessimistic"
                        />
                      </PermissionGuard> */}
                    </Stack>
                  )}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>
        <Dialog open={Boolean(popup)} onClose={() => setPopup(undefined)} maxWidth="sm" fullWidth>
          {popup}
        </Dialog>
        <AuditLogDialog
          open={auditLogOpen}
          onClose={() => setAuditLogOpen(false)}
          recordId={selectedRecord?.id}
          resource={Resources.adminConfig}
          title={auditTitle}
        />
      </DashboardContent>
    </PermissionGuard>
  );
};

export default View;
